import {useEffect, useMemo, useState} from 'react';

import {ClaudeRun, ClaudeRunStatus, RunStats} from '../lib/api';
import {
    RUN_STATUS_LABEL,
    cloudWatchLogUrl,
    formatDuration,
    formatUsd,
    repoLabel,
    runStatusClass,
    shortTaskId,
    totalUsd,
    usageTooltip,
} from '../lib/claude-runs';

import {Spinner} from './Spinner';

export type RunsRange = '24h' | '7d' | '30d';

export const RUNS_PRESET_MS: Record<RunsRange, number> = {
    '24h': 24 * 60 * 60 * 1000,
    '7d': 7 * 24 * 60 * 60 * 1000,
    '30d': 30 * 24 * 60 * 60 * 1000,
};

/** State that App owns — survives a nav away and back, like Requests/History. */
export interface ClaudeRunsPageState {
    loading: boolean;
    twingateDown: boolean;
    error?: string;
    runs?: ClaudeRun[];
    /**
     * What each session was asked and what it used, keyed by run id. Loaded separately from `runs`
     * (`undefined` while loading); absent entries mean the session reported nothing.
     */
    stats?: Record<string, RunStats>;
    statsError?: string;
    since: number;
    until: number;
    preset?: RunsRange;
}

export type ClaudeRunsLoadFn = (since: number, until: number, preset?: RunsRange) => void;

interface Props {
    state: ClaudeRunsPageState;
    onLoad: ClaudeRunsLoadFn;
}

const STATUS_OPTIONS: {value: ClaudeRunStatus | 'all'; label: string}[] = [
    {value: 'all', label: 'All'},
    {value: 'spawned', label: 'Started'},
    {value: 'denied', label: 'Denied'},
    {value: 'failed', label: 'Failed'},
];

/**
 * Admin-only audit log of the self-hosted Claude Code runner tasks: who asked for a session in
 * which environment, on which repository, and whether it was allowed to start. Read-only by
 * design — the rows are written by the orchestrators in INFRA (see
 * functions/shared/claude-runs-repo.ts); the page just lists them newest first.
 *
 * The log stops at the launch decision. How a session then ran is in CloudWatch (INFRA) and at
 * claude.ai; each row links to its log stream and to the session transcript.
 */
export function ClaudeRunsPage({state, onLoad}: Props) {
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<ClaudeRunStatus | 'all'>('all');
    const [environment, setEnvironment] = useState<string>('all');

    const runs = useMemo(() => state.runs ?? [], [state.runs]);
    const stats = state.stats;
    const statsPending = stats === undefined && !state.statsError;
    const environments = useMemo(() => Array.from(new Set(runs.map(r => r.environment))).sort(), [runs]);

    useEffect(() => {
        if (environment !== 'all' && !environments.includes(environment)) {
            setEnvironment('all');
        }
    }, [environments, environment]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return runs.filter(r => {
            if (statusFilter !== 'all') {
                // `spawning` is the brief pre-RunTask state; "Started" covers it and `spawned`.
                const effective = r.status === 'spawning' ? 'spawned' : r.status;
                if (effective !== statusFilter) {
                    return false;
                }
            }
            if (environment !== 'all' && r.environment !== environment) {
                return false;
            }
            if (!q) {
                return true;
            }
            return (
                (stats?.[r.id]?.title?.toLowerCase().includes(q) ?? false) ||
                r.email.toLowerCase().includes(q) ||
                (r.user?.toLowerCase().includes(q) ?? false) ||
                (r.repo?.toLowerCase().includes(q) ?? false) ||
                (r.revision?.toLowerCase().includes(q) ?? false) ||
                r.environment.toLowerCase().includes(q) ||
                (r.taskId?.toLowerCase().includes(q) ?? false)
            );
        });
    }, [runs, stats, search, statusFilter, environment]);

    const reload = () => onLoad(state.since, state.until, state.preset);

    if (state.twingateDown) {
        return (
            <main className="page">
                <div className="twingate-prompt">
                    <h2>Подключите Twingate</h2>
                    <p>API недоступен — подключись и нажми Reload.</p>
                    <button type="button" className="btn btn-primary" onClick={reload} disabled={state.loading}>
                        Reload
                    </button>
                </div>
            </main>
        );
    }

    return (
        <main className="page">
            <div className="page-head">
                <h1>Claude runs</h1>
                <div className="spacer" />
                <button type="button" className="btn-reload" onClick={reload} disabled={state.loading}>
                    {state.loading ? 'Loading…' : 'Reload'}
                </button>
            </div>
            <div className="page-subhead">
                Every task launch of a self-hosted Claude Code environment: who queued the session, which environment
                and repository, and whether it started. Refused launches are listed too. Request, duration and cost are
                reported by the session itself after each turn (costs are estimates: Claude at API list price, AWS
                on-demand compute); the full story is in CloudWatch (INFRA) and in the transcript at claude.ai.
            </div>

            <div className="toolbar">
                <div className="search">
                    <svg viewBox="0 0 24 24" fill="none">
                        <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                        <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <input
                        type="text"
                        placeholder="Search request, user, email, repository, branch, task id…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>

                <div className="filter-pills">
                    <span className="filter-label">Window</span>
                    <div className="toggle-group">
                        {(Object.keys(RUNS_PRESET_MS) as RunsRange[]).map(preset => (
                            <button
                                key={preset}
                                type="button"
                                className={`chip-toggle${state.preset === preset ? ' on' : ''}`}
                                onClick={() => {
                                    const until = Date.now();
                                    onLoad(until - RUNS_PRESET_MS[preset], until, preset);
                                }}
                            >
                                {preset}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="filter-pills">
                    <span className="filter-label">Status</span>
                    <select
                        className="filter-select"
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value as ClaudeRunStatus | 'all')}
                        aria-label="Filter by status"
                    >
                        {STATUS_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                </div>

                {environments.length > 1 && (
                    <div className="filter-pills">
                        <span className="filter-label">Environment</span>
                        <select
                            className="filter-select"
                            value={environment}
                            onChange={e => setEnvironment(e.target.value)}
                            aria-label="Filter by environment"
                        >
                            <option value="all">All</option>
                            {environments.map(env => (
                                <option key={env} value={env}>
                                    {env}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                <div className="toolbar-spacer" />
                <div className="toolbar-summary">
                    {filtered.length} of {runs.length} runs
                    {state.statsError && (
                        <span className="stats-error" title={state.statsError}>
                            {' · '}request / cost unavailable
                        </span>
                    )}
                </div>
            </div>

            <div className="card">
                {state.loading ? (
                    <Spinner label="Loading Claude runs…" />
                ) : state.error ? (
                    <div className="empty">
                        <h3>Failed to load</h3>
                        <div>{state.error}</div>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="empty">
                        <div>
                            {runs.length === 0
                                ? 'No Claude sessions were started in this window.'
                                : 'No runs match the current filters.'}
                        </div>
                    </div>
                ) : (
                    <table className="tbl">
                        <thead>
                            <tr>
                                <th className="col-when">When</th>
                                <th>User</th>
                                <th>Request</th>
                                <th>Repository</th>
                                <th>Status</th>
                                <th>Duration</th>
                                <th>Cost</th>
                                <th>Links</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(run => (
                                <RunRow key={run.id} run={run} stats={stats?.[run.id]} statsPending={statsPending} />
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </main>
    );
}

function RunRow({run, stats, statsPending}: {run: ClaudeRun; stats?: RunStats; statsPending: boolean}) {
    const logUrl = cloudWatchLogUrl(run);
    const taskId = shortTaskId(run);
    let detail: string | undefined;
    if (run.status === 'denied') {
        detail = run.denyReason;
    } else if (run.status === 'failed') {
        detail = run.failureReason;
    }
    return (
        <tr>
            <td className="col-when">
                <div className="when-cell">
                    <div>{formatRelative(run.requestedAt)}</div>
                    <div className="when-abs">{formatAbsolute(run.requestedAt)}</div>
                </div>
            </td>
            <td>
                <div className="when-cell">
                    <div>{run.user ?? run.email}</div>
                    {run.user && <div className="when-abs">{run.email}</div>}
                </div>
            </td>
            <td className="col-request">
                <RequestCell run={run} stats={stats} pending={statsPending} />
            </td>
            <td>
                <div className="when-cell">
                    <div>{repoLabel(run)}</div>
                    <div className="when-abs">{run.environment}</div>
                </div>
            </td>
            <td>
                <div className="when-cell">
                    <span className={`status-chip ${runStatusClass(run.status)}`}>{RUN_STATUS_LABEL[run.status]}</span>
                    {detail && (
                        <div className="when-abs" title={detail}>
                            {detail}
                        </div>
                    )}
                </div>
            </td>
            <td>
                <div className="when-cell">
                    <div>{formatDuration(stats?.runtimeMs) ?? (hasTask(run) && statsPending ? '…' : '—')}</div>
                    {run.size && <div className="when-abs">{run.size}</div>}
                </div>
            </td>
            <td>
                <CostCell stats={stats} pending={hasTask(run) && statsPending} />
            </td>
            <td>
                <div className="when-cell">
                    <div>
                        {run.sessionUrl ? (
                            <a href={run.sessionUrl} target="_blank" rel="noreferrer">
                                Session ↗
                            </a>
                        ) : (
                            '—'
                        )}
                        {logUrl && (
                            <>
                                {' · '}
                                <a href={logUrl} target="_blank" rel="noreferrer">
                                    Logs ↗
                                </a>
                            </>
                        )}
                    </div>
                    {taskId && <div className="when-abs">task …{taskId}</div>}
                </div>
            </td>
        </tr>
    );
}

function hasTask(run: ClaudeRun): boolean {
    return run.status === 'spawned' || run.status === 'spawning';
}

function RequestCell({run, stats, pending}: {run: ClaudeRun; stats?: RunStats; pending: boolean}) {
    if (stats?.title) {
        const prompts = stats.prompts ?? 0;
        return (
            <div className="when-cell">
                <div className="request-title" title={stats.title}>
                    {stats.title}
                </div>
                {prompts > 1 && <div className="when-abs">{prompts} prompts</div>}
            </div>
        );
    }
    if (hasTask(run) && pending) {
        return <span className="muted">…</span>;
    }
    return <span className="muted">—</span>;
}

function CostCell({stats, pending}: {stats?: RunStats; pending: boolean}) {
    const total = formatUsd(totalUsd(stats));
    if (!stats || !total) {
        return <span className="muted">{pending ? '…' : '—'}</span>;
    }
    const parts = [
        stats.awsUsd !== undefined ? `AWS ${formatUsd(stats.awsUsd)}` : undefined,
        stats.claudeUsd !== undefined ? `Claude ${formatUsd(stats.claudeUsd)}` : undefined,
    ].filter(Boolean);
    return (
        <div className="when-cell" title={usageTooltip(stats)}>
            <div>
                ≈ {total}
                {stats.unpricedModels.length > 0 && '+'}
            </div>
            <div className="when-abs">{parts.join(' · ')}</div>
        </div>
    );
}

function formatRelative(ts: number): string {
    if (!Number.isFinite(ts)) {
        return '';
    }
    const diff = Date.now() - ts;
    if (diff < 60_000) {
        return 'just now';
    }
    if (diff < 3_600_000) {
        return `${Math.round(diff / 60_000)}m ago`;
    }
    if (diff < 86_400_000) {
        return `${Math.round(diff / 3_600_000)}h ago`;
    }
    return `${Math.round(diff / 86_400_000)}d ago`;
}

function formatAbsolute(ts: number): string {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) {
        return '';
    }
    return d.toLocaleString(undefined, {month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'});
}
